import { Fragment } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import Loading from 'src/components/global/Loading';
import type { DurationGroup, DurationMonthValue } from './types';

type ApplicationsByStateWithDurationTableProps = {
  months: string[];
  groups: DurationGroup[];
  totalRow: DurationMonthValue[];
  loading?: boolean;
};

const topHeaderStyle = {
  fontWeight: 700,
  color: '#ffffff',
  backgroundColor: '#25333d',
  borderBottom: '1px solid #9ea7ad',
  fontSize: 12,
  padding: '8px 6px',
};

const subHeaderStyle = {
  fontWeight: 600,
  color: '#5a5f63',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #9ea7ad',
  fontSize: 12,
  padding: '6px 6px',
};

const bodyCellStyle = {
  fontSize: 12,
  color: '#5a5f63',
  borderBottom: '1px solid #d4d7da',
  padding: '6px 6px',
};

const leftCellStyle = {
  ...bodyCellStyle,
  textAlign: 'left' as const,
};

const rightCellStyle = {
  ...bodyCellStyle,
  textAlign: 'right' as const,
};

const subtotalCellStyle = {
  ...bodyCellStyle,
  fontWeight: 700,
  color: '#404448',
  textAlign: 'right' as const,
};

const subtotalTextStyle = {
  ...bodyCellStyle,
  fontWeight: 700,
  color: '#404448',
  textAlign: 'left' as const,
};

const totalCellStyle = {
  ...bodyCellStyle,
  fontWeight: 700,
  color: '#404448',
  backgroundColor: '#fafafa',
  borderTop: '2px solid #6b6f72',
  paddingTop: 8,
  paddingBottom: 8,
};

const totalRightCellStyle = {
  ...totalCellStyle,
  textAlign: 'right' as const,
};

export const ApplicationsByStateWithDurationTable = ({
  months,
  groups,
  totalRow,
  loading = false,
}: ApplicationsByStateWithDurationTableProps) => (
  <Paper
    elevation={1}
    style={{
      borderRadius: 6,
      overflow: 'hidden',
      background: '#ffffff',
    }}
  >
    <TableContainer style={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={topHeaderStyle} colSpan={2}>
              &nbsp;
            </TableCell>
            <TableCell style={topHeaderStyle} colSpan={Math.max(months.length * 2, 2)} align="right">
              FECHA DE DESEMBOLSO / CANTIDAD / IMPORTE
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              rowSpan={2}
              style={{
                ...subHeaderStyle,
                textAlign: 'left',
                verticalAlign: 'bottom',
                minWidth: 110,
              }}
            >
              ESTADO
            </TableCell>

            <TableCell
              rowSpan={2}
              style={{
                ...subHeaderStyle,
                textAlign: 'left',
                verticalAlign: 'bottom',
                minWidth: 160,
                maxWidth: 160,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              ESTADO DIAS
            </TableCell>

            {months.map((month) => (
              <TableCell key={month} colSpan={2} align="center" style={subHeaderStyle}>
                {month}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            {months.map((month) => (
              <Fragment key={month}>
                <TableCell align="right" style={subHeaderStyle}># Cantidad</TableCell>
                <TableCell align="right" style={subHeaderStyle}>S/. Importe</TableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={Math.max(months.length * 2 + 2, 4)}>
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <Loading />
                </Box>
              </TableCell>
            </TableRow>
          ) : null}

          {!loading && groups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={Math.max(months.length * 2 + 2, 4)}>
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <Typography style={{ color: '#6b6b6b', fontSize: 14 }}>Sin informacion disponible.</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : null}

          {!loading && groups.map((group) => (
            <Fragment key={group.state}>
              {group.details.map((detail, index) => (
                <TableRow key={`${group.state}-${detail.stateDays}`}>
                  {index === 0 ? (
                    <TableCell
                      rowSpan={group.details.length + 1}
                      style={{
                        ...leftCellStyle,
                        verticalAlign: 'top',
                        minWidth: 110,
                      }}
                    >
                      {group.state}
                    </TableCell>
                  ) : null}

                  <TableCell
                    style={{
                      ...leftCellStyle,
                      minWidth: 160,
                      maxWidth: 160,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={detail.stateDays}
                  >
                    {detail.stateDays}
                  </TableCell>

                  {detail.values.map((value, valueIndex) => (
                    <Fragment key={`${group.state}-${detail.stateDays}-${valueIndex}` as string}>
                      <TableCell style={rightCellStyle}>{value.quantity}</TableCell>
                      <TableCell style={rightCellStyle}>{value.amount}</TableCell>
                    </Fragment>
                  ))}
                </TableRow>
              ))}

              <TableRow>
                <TableCell style={subtotalTextStyle}>Subtotal</TableCell>
                {group.subtotal.map((value, valueIndex) => (
                  <Fragment key={`${group.state}-subtotal-${valueIndex}` as string}>
                    <TableCell style={subtotalCellStyle}>{value.quantity}</TableCell>
                    <TableCell style={subtotalCellStyle}>{value.amount}</TableCell>
                  </Fragment>
                ))}
              </TableRow>
            </Fragment>
          ))}

          {!loading && totalRow.length > 0 ? (
            <TableRow>
              <TableCell colSpan={2} style={totalCellStyle}>Total</TableCell>
              {totalRow.map((value, index) => (
                <Fragment key={`total-${index}` as string}>
                  <TableCell style={totalRightCellStyle}>{value.quantity}</TableCell>
                  <TableCell style={totalRightCellStyle}>{value.amount}</TableCell>
                </Fragment>
              ))}
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);
